import React, { useState, useMemo, useCallback } from 'react'
import { connect } from 'react-redux'
import { Box, Button, TextField, MenuItem, Select } from '@material-ui/core'
import TableData from '../TableData'
import useStyles from './styles'
import {
  SortOrder, MONTHS, DEFAULT_MONTH_KEY, COLUMN_IDS, TABLE_HEADINGS,
  CSV_FILE_PREFIX, GRAND_TOTAL_LABEL, ALL_ACCOUNTS_LABEL, ALL_CURRENCIES_LABEL,
  ALL_MONTHS_LABEL, EMPTY_TABLE_TEXT, FETCH_ERROR_MESSAGE,
} from './constants'
import Search from '../../../../elements/icons/Search'
import Download from '../../../../elements/icons/Download'
import { DisplayMessage, Loader } from '../../../../Components'
import AppButton from '../../../../Components/AppButton'
import { eventService } from '../../../../Services/EventService'
import { getErrorMessage } from '../constants'
import * as messageTypes from '../../../../utils/messageTypes'

const fmtMonth = (monthKey) => {
  if (!monthKey || monthKey.length !== 6) return monthKey ?? ''
  return MONTHS[parseInt(monthKey.slice(4, 6), 10) - 1] + " '" + monthKey.slice(2, 4)
}

const fmtNum = (number) => {
  if (number == null) return '—'
  if (number === 0) return '0'
  if (number >= 1e9) return (number / 1e9).toFixed(2).replace(/\.?0+$/, '') + 'B'
  if (number >= 1e6) return (number / 1e6).toFixed(2).replace(/\.?0+$/, '') + 'M'
  if (number >= 1e3) return (number / 1e3).toFixed(1).replace(/\.?0+$/, '') + 'K'
  return number.toLocaleString()
}

const fmtAmt = (value, currency) => {
  if (value == null) return '—'
  const symbol = currency === 'EUR' ? '€' : '$'
  return symbol + (Math.round(value * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const escapeCsvValue = (value) => {
  const stringValue = value == null ? '' : String(value)
  return stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n') ? `"${stringValue.replace(/"/g, '""')}"` : stringValue
}

const InvoiceViewer = (props) => {
  const classes = useStyles()
  const [allData, setAllData] = useState([])
  const [sortBy, setSortBy] = useState(null)
  const [sortOrder, setSortOrder] = useState(SortOrder.DESC)
  const [searchQuery, setSearchQuery] = useState('')
  const [accountFilter, setAccountFilter] = useState('')
  const [currencyFilter, setCurrencyFilter] = useState('')
  const [monthFilter, setMonthFilter] = useState('')
  const [expandedConfigs, setExpandedConfigs] = useState({})
  const [monthKey, setMonthKey] = useState(DEFAULT_MONTH_KEY)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')

  const processData = useCallback((json) => {
    const data = json.data ?? []
    if (!Array.isArray(data)) {
      setMessage('Invalid response: data is not an array')
      setMessageType(messageTypes.ERROR)
      setOpen(true)
      return
    }
    setAllData(data)
    if (data.length === 0) {
      setMessage('No invoices found for this month')
      setMessageType(messageTypes.ERROR)
      setOpen(true)
    }
    setSearchQuery('')
    setAccountFilter('')
    setCurrencyFilter('')
    setMonthFilter('')
    setSortBy(null)
    setExpandedConfigs({})
  }, [])

  const fetchData = () => {
    setLoading(true)
    eventService.getInvoices(monthKey.trim(), props.currentUser.account_id)
      .then(data => {
        processData(data)
        setMessage(`Loaded ${data.data?.length ?? 0} invoices`)
        setMessageType(messageTypes.SUCCESS)
        setOpen(true)
      })
      .catch(error => {
        setMessage(getErrorMessage(error, FETCH_ERROR_MESSAGE))
        setMessageType(messageTypes.ERROR)
        setOpen(true)
      })
      .finally(() => setLoading(false))
  }

  const filteredData = useMemo(() => {
    const query = searchQuery.toLowerCase()
    return allData.filter(row => {
      if (accountFilter && row.accountName !== accountFilter) return false
      if (currencyFilter && row.config?.billingCurrency !== currencyFilter) return false
      if (monthFilter && row.monthKey !== monthFilter) return false
      if (query && !row.accountName?.toLowerCase().includes(query) && !String(row.accountId ?? '').toLowerCase().includes(query)) return false
      return true
    })
  }, [allData, searchQuery, accountFilter, currencyFilter, monthFilter])

  const sortedData = useMemo(() => {
    if (!sortBy) return filteredData
    const getSortValue = (row) => {
      switch (sortBy) {
        case COLUMN_IDS.ACCOUNT:
          return row.accountName ?? ''
        case COLUMN_IDS.MONTH:
          return row.monthKey ?? ''
        case COLUMN_IDS.CURRENCY:
          return row.config?.billingCurrency ?? ''
        case COLUMN_IDS.TOTAL_API_HITS:
          return row.totalApiHits ?? 0
        case COLUMN_IDS.FREE_HITS:
          return row.freeHitsAllotted ?? 0
        case COLUMN_IDS.OVERAGE_HITS:
          return row.overageApiHits ?? 0
        case COLUMN_IDS.API_OVERAGE_FEES:
          return row.totalApiOverageFees ?? 0
        case COLUMN_IDS.ACTIVE_SITES:
          return row.activeSitesCount ?? 0
        case COLUMN_IDS.BILLABLE_SITES:
          return row.billableSitesCount ?? 0
        case COLUMN_IDS.SITES_FEES:
          return row.totalActiveSitesFees ?? 0
        case COLUMN_IDS.HRT_SITES:
          return row.hrtSitesCount ?? 0
        case COLUMN_IDS.HRT_FEES:
          return row.totalHrtSitesFees ?? 0
        case COLUMN_IDS.FIXED_FEES:
          return row.fixedFees ?? 0
        case COLUMN_IDS.TOTAL_AMOUNT:
          return row.totalAmount ?? 0
        case COLUMN_IDS.TAX_AMOUNT:
          return row.taxAmount ?? 0
        case COLUMN_IDS.TOTAL_INVOICE:
          return row.totalAmountWithTax ?? 0
        default:
          return 0
      }
    }
    return [...filteredData].sort((rowA, rowB) => {
      const valueA = getSortValue(rowA), valueB = getSortValue(rowB)
      const comparison = typeof valueA === 'string' ? valueA.localeCompare(valueB) : (valueA < valueB ? -1 : valueA > valueB ? 1 : 0)
      return comparison * (sortOrder === SortOrder.ASC ? 1 : -1)
    })
  }, [filteredData, sortBy, sortOrder])

  const statsByCurrency = useMemo(() => {
    const byCurrency = {}
    filteredData.forEach(row => {
      const currency = row.config?.billingCurrency ?? 'USD'
      if (!byCurrency[currency]) byCurrency[currency] = { total: 0, count: 0 }
      byCurrency[currency].total += row.totalAmount ?? 0
      byCurrency[currency].count += 1
    })
    return byCurrency
  }, [filteredData])

  const totalApiHits = useMemo(() => filteredData.reduce((sum, row) => sum + (row.totalApiHits ?? 0), 0), [filteredData])
  const totalActiveSites = useMemo(() => filteredData.reduce((sum, row) => sum + (row.activeSitesCount ?? 0), 0), [filteredData])

  const tableHeadings = TABLE_HEADINGS.map(heading => ({
    ...heading,
    sortOrder: heading.sortOrder === null ? null : (sortBy === heading.id ? sortOrder : SortOrder.ASC),
  }))

  const tableContents = useMemo(() => {
    const rows = sortedData.map(row => ({ ...row }))
    const byCurrency = {}
    sortedData.forEach(row => {
      const currency = row.config?.billingCurrency ?? 'USD'
      if (!byCurrency[currency]) byCurrency[currency] = { apiOverage: 0, sitesFees: 0, hrtFees: 0, fixed: 0, total: 0, taxAmount: 0, totalWithTax: 0, count: 0 }
      byCurrency[currency].apiOverage += row.totalApiOverageFees ?? 0
      byCurrency[currency].sitesFees += row.totalActiveSitesFees ?? 0
      byCurrency[currency].hrtFees += row.totalHrtSitesFees ?? 0
      byCurrency[currency].fixed += row.fixedFees ?? 0
      byCurrency[currency].total += row.totalAmount ?? 0
      byCurrency[currency].taxAmount += row.taxAmount ?? 0
      byCurrency[currency].totalWithTax += row.totalAmountWithTax ?? 0
      byCurrency[currency].count += 1
    })
    Object.entries(byCurrency).sort(([currencyA], [currencyB]) => currencyA.localeCompare(currencyB)).forEach(([currency, totals]) => {
      rows.push({ id: `__total_${currency}__`, _isCurrencyTotal: true, _currency: currency, _totals: totals })
    })
    return rows
  }, [sortedData])

  const accountNames = useMemo(() => [...new Set(allData.map(row => row.accountName))].filter(Boolean).sort(), [allData])
  const currencies = useMemo(() => [...new Set(allData.map(row => row.config?.billingCurrency))].filter(Boolean).sort(), [allData])
  const monthKeys = useMemo(() => [...new Set(allData.map(row => row.monthKey))].filter(Boolean).sort(), [allData])

  const renderCellContent = (item, heading) => {
    const currency = item.config?.billingCurrency ?? item._currency ?? 'USD'

    if (item._isCurrencyTotal) {
      const totals = item._totals
      switch (heading.id) {
        case COLUMN_IDS.ACCOUNT: return `${GRAND_TOTAL_LABEL} (${item._currency}) — ${totals.count} rows`
        case COLUMN_IDS.API_OVERAGE_FEES: return totals.apiOverage > 0 ? fmtAmt(totals.apiOverage, item._currency) : <Box component="span" className={classes.dash}>—</Box>
        case COLUMN_IDS.SITES_FEES: return totals.sitesFees > 0 ? fmtAmt(totals.sitesFees, item._currency) : <Box component="span" className={classes.dash}>—</Box>
        case COLUMN_IDS.HRT_FEES: return totals.hrtFees > 0 ? fmtAmt(totals.hrtFees, item._currency) : <Box component="span" className={classes.dash}>—</Box>
        case COLUMN_IDS.FIXED_FEES: return totals.fixed > 0 ? fmtAmt(totals.fixed, item._currency) : <Box component="span" className={classes.dash}>—</Box>
        case COLUMN_IDS.TOTAL_AMOUNT: return fmtAmt(totals.total, item._currency)
        case COLUMN_IDS.TAX_AMOUNT: return totals.taxAmount > 0 ? fmtAmt(totals.taxAmount, item._currency) : <Box component="span" className={classes.dash}>—</Box>
        case COLUMN_IDS.TOTAL_INVOICE: return fmtAmt(totals.totalWithTax, item._currency)
        default: return ''
      }
    }

    switch (heading.id) {
      case COLUMN_IDS.ACCOUNT:
        return (
          <Box>
            <Box className={classes.acctName}>{item.accountName}</Box>
            <Box className={classes.acctId}>{item.accountId ?? ''}</Box>
          </Box>
        )
      case COLUMN_IDS.MONTH: return fmtMonth(item.monthKey)
      case COLUMN_IDS.CURRENCY: {
        const currencyClass = currency === 'USD' ? classes.curUsd : currency === 'EUR' ? classes.curEur : classes.curOther
        return <Box component="span" className={`${classes.curBadge} ${currencyClass}`}>{currency}</Box>
      }
      case COLUMN_IDS.TOTAL_API_HITS: return fmtNum(item.totalApiHits)
      case COLUMN_IDS.FREE_HITS: return fmtNum(item.freeHitsAllotted)
      case COLUMN_IDS.OVERAGE_HITS: return fmtNum(item.overageApiHits)
      case COLUMN_IDS.API_OVERAGE_FEES:
        return (item.totalApiOverageFees ?? 0) > 0 ? fmtAmt(item.totalApiOverageFees, currency) : <Box component="span" className={classes.dash}>—</Box>
      case COLUMN_IDS.ACTIVE_SITES: return (item.activeSitesCount ?? 0).toLocaleString()
      case COLUMN_IDS.BILLABLE_SITES: return (item.billableSitesCount ?? 0).toLocaleString()
      case COLUMN_IDS.SITES_FEES:
        return (item.totalActiveSitesFees ?? 0) > 0 ? fmtAmt(item.totalActiveSitesFees, currency) : <Box component="span" className={classes.dash}>—</Box>
      case COLUMN_IDS.HRT_SITES: return (item.hrtSitesCount ?? 0).toLocaleString()
      case COLUMN_IDS.HRT_FEES:
        return (item.totalHrtSitesFees ?? 0) > 0 ? fmtAmt(item.totalHrtSitesFees, currency) : <Box component="span" className={classes.dash}>—</Box>
      case COLUMN_IDS.FIXED_FEES:
        return (item.fixedFees ?? 0) > 0 ? fmtAmt(item.fixedFees, currency) : <Box component="span" className={classes.dash}>—</Box>
      case COLUMN_IDS.TOTAL_AMOUNT: return fmtAmt(item.totalAmount, currency)
      case COLUMN_IDS.TAX_AMOUNT:
        return (item.taxAmount ?? 0) > 0 ? fmtAmt(item.taxAmount, currency) : <Box component="span" className={classes.dash}>—</Box>
      case COLUMN_IDS.TOTAL_INVOICE: return fmtAmt(item.totalAmountWithTax, currency)
      case COLUMN_IDS.CONFIG:
        return (
          <AppButton
            className={classes.configBtn}
            minHeight='16px'
            text="⚙ Config"
            fill={true}
            onClick={() => setExpandedConfigs(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
          />
        )
      default: return ''
    }
  }

  const getCellClassName = (item, heading) => {
    if (item._isCurrencyTotal) {
      if (heading.id === COLUMN_IDS.ACCOUNT) return classes.currencyTotalAccountCell
      if (heading.id === COLUMN_IDS.TOTAL_AMOUNT) return classes.currencyTotalAmountCell
      if (heading.id === COLUMN_IDS.TOTAL_INVOICE) return classes.currencyTotalInvoiceCell
      return classes.currencyTotalCell
    }
    if (heading.id === COLUMN_IDS.ACCOUNT) return classes.accountCell
    if (heading.id === COLUMN_IDS.TOTAL_AMOUNT) return classes.amountCell
    if (heading.id === COLUMN_IDS.TOTAL_INVOICE) return classes.totalInvoiceCell
    return classes.titleWrapper
  }

  const renderExpandedRow = (item, index, colSpan) => {
    if (item._isCurrencyTotal || !expandedConfigs[item.id]) return null
    const config = item.config ?? {}
    const currency = config.billingCurrency ?? 'USD'
    const symbol = currency === 'EUR' ? '€' : '$'
    const boolValue = (value) => <Box component="span" className={value ? classes.boolYes : classes.boolNo}>{value ? 'YES' : 'NO'}</Box>

    const renderConfigRow = (label, value) => (
      <Box className={classes.cfgTableRow}>
        <Box className={classes.cfgTableCell}>{label}</Box>
        <Box className={classes.cfgTableCellValue}>{value ?? <Box component="span" className={classes.dash}>—</Box>}</Box>
      </Box>
    )

    const billingSection = (
      <Box className={classes.cfgSection}>
        <Box className={classes.cfgSectionTitle}>Billing</Box>
        <Box className={classes.cfgTable}>
          {renderConfigRow('Billable', boolValue(config.isBillable))}
          {renderConfigRow('Type', config.billingType ?? '—')}
          {renderConfigRow('Currency', currency)}
          {renderConfigRow('Fixed Monthly', `${symbol}${(config.fixedMonthlyFees ?? 0).toFixed(2)}`)}
          {renderConfigRow('Fixed Yearly', `${symbol}${(config.fixedYearlyFees ?? 0).toFixed(2)}`)}
          {renderConfigRow('Yearly Billing Month', config.yearlyBillingMonth ?? '—')}
          {config.vatPercent != null ? renderConfigRow('VAT %', `${config.vatPercent}%`) : null}
        </Box>
      </Box>
    )

    const apiSection = (
      <Box className={classes.cfgSection}>
        <Box className={classes.cfgSectionTitle}>API Hits</Box>
        <Box className={classes.cfgTable}>
          {renderConfigRow('Billable', boolValue(config.apiHitsBillable))}
          {renderConfigRow('Free Hits / Site', (config.monthlyFreeHitsPerSite ?? 0).toLocaleString())}
          {config.monthlyFreeHits > 0 ? renderConfigRow('Global Free Hits', (config.monthlyFreeHits).toLocaleString()) : null}
          {config.slabHitsEnabled
            ? (
              <>
                {renderConfigRow('Slab Size', (config.slabSize ?? 0).toLocaleString())}
                {renderConfigRow('Overage / Slab', `${symbol}${config.overageFeesPerSlab ?? 0}`)}
              </>
            )
            : renderConfigRow('Overage / Hit', `${symbol}${config.overageFeesPerHit ?? 0}`)
          }
        </Box>
      </Box>
    )

    const sitesSection = (
      <Box className={classes.cfgSection}>
        <Box className={classes.cfgSectionTitle}>Active Sites</Box>
        <Box className={classes.cfgTable}>
          {renderConfigRow('Billable', boolValue(config.activeSitesBillable))}
          {renderConfigRow('Billable Type', config.activeSitesBillableType ?? '—')}
          {renderConfigRow('Fee / Site', `${symbol}${(config.monthlyFeesPerActiveSite ?? 0).toFixed(2)}`)}
          {config.monthlyFreeActiveSites != null ? renderConfigRow('Free Sites', (config.monthlyFreeActiveSites).toLocaleString()) : null}
        </Box>
      </Box>
    )

    const hrtSection = (
      <Box className={classes.cfgSection}>
        <Box className={classes.cfgSectionTitle}>HRT Sites</Box>
        <Box className={classes.cfgTable}>
          {renderConfigRow('Billable', boolValue(config.hrtSitesBillable))}
          {renderConfigRow('Fee / Site', `${symbol}${(config.monthlyFeesPerHrtSite ?? 0).toFixed(2)}`)}
        </Box>
      </Box>
    )

    const extras = []
    if (config.childAccountIds?.length > 0) {
      extras.push(renderConfigRow('Child Accounts', config.childAccountIds.length))
    }
    if (config.excludedProgramIdsForActiveSites?.length > 0) {
      extras.push(renderConfigRow('Excluded Programs (sites)', config.excludedProgramIdsForActiveSites.length))
    }
    const extraSection = extras.length > 0 ? (
      <Box className={classes.cfgSection}>
        <Box className={classes.cfgSectionTitle}>Extras</Box>
        <Box className={classes.cfgTable}>
          {extras}
        </Box>
      </Box>
    ) : null

    return (
      <>
        <Box className={classes.configTitle}>
          Billing config for <strong>{item.accountName}</strong>
          <Box component="span" className={classes.configId}>{config.id ?? ''}</Box>
        </Box>
        <Box className={classes.cfgGrid}>
          {billingSection}
          {apiSection}
          {sitesSection}
          {hrtSection}
          {extraSection}
        </Box>
      </>
    )
  }

  const exportCsv = () => {
    if (!filteredData.length) return
    const header = [
      'Account Name', 'Account ID', 'Month', 'Currency',
      'Total API Hits', 'Free Hits Allotted', 'Overage API Hits', 'API Overage Fees',
      'Active Sites', 'Billable Sites', 'Active Sites Fees',
      'HRT Sites', 'HRT Fees', 'Fixed Fees', 'Total Amount', 'Tax Amount', 'Total Invoice', 'Status',
    ].map(escapeCsvValue).join(',')
    const body = filteredData.map(row => [
      escapeCsvValue(row.accountName ?? ''),
      row.accountId ?? '',
      row.monthKey ?? '',
      row.config?.billingCurrency ?? '',
      row.totalApiHits ?? 0,
      row.freeHitsAllotted ?? 0,
      row.overageApiHits ?? 0,
      (row.totalApiOverageFees ?? 0).toFixed(2),
      row.activeSitesCount ?? 0,
      row.billableSitesCount ?? 0,
      (row.totalActiveSitesFees ?? 0).toFixed(2),
      row.hrtSitesCount ?? 0,
      (row.totalHrtSitesFees ?? 0).toFixed(2),
      (row.fixedFees ?? 0).toFixed(2),
      (row.totalAmount ?? 0).toFixed(2),
      (row.taxAmount ?? 0).toFixed(2),
      (row.totalAmountWithTax ?? 0).toFixed(2),
      row.status ?? '',
    ].join(','))
    const csv = '\uFEFF' + [header, ...body].join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const anchor = document.createElement('a')
    anchor.href = URL.createObjectURL(blob)
    anchor.download = `${CSV_FILE_PREFIX}_${new Date().toISOString().slice(0, 10)}.csv`
    anchor.click()
    URL.revokeObjectURL(anchor.href)
  }

  const chipGradient = (currency) => currency === 'EUR'
    ? 'linear-gradient(135deg,#1e3a8a,#2563eb)'
    : 'linear-gradient(135deg,#065f46,#059669)'

  return (
    <Box>
      <DisplayMessage open={open} message={message} messageType={messageType} handleClose={() => setOpen(false)} />
      <Loader loading={loading} />

      <Box className={classes.urlBar}>
        <Box className={classes.urlRow}>
          <Box className={classes.urlBarLabel}>Month Key For Invoices</Box>
          <TextField
            className={classes.monthInput}
            value={monthKey}
            onChange={(e) => setMonthKey(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') fetchData() }}
            placeholder="202609"
            variant="standard"
            InputProps={{ disableUnderline: true }}
          />
          <Button className={classes.submitBtn} onClick={fetchData} disabled={loading}>
            <Search />
            <Box paddingLeft="10px">Fetch &amp; Render</Box>
          </Button>
        </Box>
      </Box>

      {allData.length > 0 && (
        <Box className={classes.statsBar}>
          <Box className={classes.statChip} style={{ background: 'linear-gradient(135deg,#1e40af,#2563eb)' }}>
            <Box className={classes.statVal}>{filteredData.length}</Box>
            <Box className={classes.statLbl}>Invoices</Box>
            <Box className={classes.statSub}>{[...new Set(filteredData.map(row => row.accountName))].length} accounts</Box>
          </Box>
          {Object.entries(statsByCurrency).sort(([currencyA], [currencyB]) => currencyA.localeCompare(currencyB)).map(([currency, stats]) => (
            <Box key={currency} className={classes.statChip} style={{ background: chipGradient(currency) }}>
              <Box className={classes.statVal}>{fmtAmt(stats.total, currency)}</Box>
              <Box className={classes.statLbl}>Total {currency}</Box>
              <Box className={classes.statSub}>{stats.count} accounts</Box>
            </Box>
          ))}
          <Box className={classes.statChip} style={{ background: 'linear-gradient(135deg,#4c1d95,#7c3aed)' }}>
            <Box className={classes.statVal}>{fmtNum(totalApiHits)}</Box>
            <Box className={classes.statLbl}>Total API Hits</Box>
          </Box>
          <Box className={classes.statChip} style={{ background: 'linear-gradient(135deg,#7c2d12,#c2410c)' }}>
            <Box className={classes.statVal}>{totalActiveSites.toLocaleString()}</Box>
            <Box className={classes.statLbl}>Total Active Sites</Box>
          </Box>
        </Box>
      )}

      <Box className={classes.container}>
        <Box className={classes.card}>
          <Box className={classes.filterBar}>
            <TextField
              className={classes.searchInput}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search account name or ID…"
              variant="standard"
              InputProps={{ disableUnderline: true }}
            />
            <Select
              className={classes.filterSelect}
              value={accountFilter}
              onChange={(e) => setAccountFilter(e.target.value)}
              displayEmpty
              disableUnderline
              MenuProps={{ classes: { paper: classes.selectPaper }, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, getContentAnchorEl: null }}
            >
              <MenuItem value="">{ALL_ACCOUNTS_LABEL}</MenuItem>
              {accountNames.map(name => <MenuItem key={name} value={name}>{name}</MenuItem>)}
            </Select>
            <Select
              className={classes.filterSelect}
              value={currencyFilter}
              onChange={(e) => setCurrencyFilter(e.target.value)}
              displayEmpty
              disableUnderline
              MenuProps={{ classes: { paper: classes.selectPaper }, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, getContentAnchorEl: null }}
            >
              <MenuItem value="">{ALL_CURRENCIES_LABEL}</MenuItem>
              {currencies.map(currency => <MenuItem key={currency} value={currency}>{currency}</MenuItem>)}
            </Select>
            <Select
              className={classes.filterSelect}
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              displayEmpty
              disableUnderline
              MenuProps={{ classes: { paper: classes.selectPaper }, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, getContentAnchorEl: null }}
            >
              <MenuItem value="">{ALL_MONTHS_LABEL}</MenuItem>
              {monthKeys.map(month => <MenuItem key={month} value={month}>{fmtMonth(month)}</MenuItem>)}
            </Select>
            <Box className={classes.filterRight}>
              <Button className={classes.exportBtn} onClick={exportCsv} disabled={!filteredData.length}>
                <Download />
                <Box paddingLeft="5px">Export CSV</Box>
              </Button>
            </Box>
          </Box>

          <TableData
            classes={classes}
            tableHeadings={tableHeadings}
            tableContents={tableContents}
            isBold={sortBy}
            handleSortChange={(column, direction) => { setSortBy(column); setSortOrder(direction) }}
            renderCellContent={renderCellContent}
            getCellClassName={getCellClassName}
            renderInvoiceExpandedRow={renderExpandedRow}
            containerClassName={classes.tableWrap}
            stickyHeader={false}
            emptyTableText={EMPTY_TABLE_TEXT}
          />
        </Box>
      </Box>
    </Box>
  )
}

const mapStateToProps = ({ auth }) => {
  const { currentUser } = auth
  return { currentUser }
}

export default connect(mapStateToProps)(InvoiceViewer)